import React from 'react';
import {Box, Chip, Stack} from '@mui/material';

/**
 * Side-by-side dual-language wrapper.
 *
 *   ┌─────────────────┬─────────────────┐
 *   │  English        │  Persian        │   ← language label chips (sticky)
 *   ├─────────────────┼─────────────────┤
 *   │  left content   │  right content  │
 *   └─────────────────┴─────────────────┘
 *
 * English is always the left/first column; the original (foreign) language is
 * the right column with a clearly labeled chip. A vertical draggable divider
 * resizes the split. When `right` is null/omitted (source already English, or
 * no translation) the wrapper renders the left column full-width with no chip.
 *
 * Columns keep a min-width and the container scrolls horizontally when the
 * panel is too narrow, so the original-language column is never clipped away
 * by a flexbox shrink.
 */

const MIN_LEFT_PX = 220;
const MIN_RIGHT_PX = 220;
const DEFAULT_LEFT_PERCENT = 50;

const LanguageHeader = ({label, color}) => (
    <Box
        sx={(theme) => ({
            position: 'sticky',
            top: 0,
            zIndex: 2,
            px: 1.5,
            py: 0.75,
            bgcolor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
        })}
    >
        <Chip
            size="small"
            label={label}
            color={color}
            variant={color === 'primary' ? 'filled' : 'outlined'}
            sx={{height: 20, fontSize: 11, fontWeight: 600, '& .MuiChip-label': {px: 1}}}
        />
    </Box>
);

const DualLanguagePanes = ({
    left,
    right,
    leftLabel = 'English',
    rightLabel = 'Original',
}) => {
    const showRight = right != null;
    const [leftPercent, setLeftPercent] = React.useState(DEFAULT_LEFT_PERCENT);
    const containerRef = React.useRef(null);
    const isDragging = React.useRef(false);

    // Synchronized scrolling: when either column scrolls, mirror the scroll
    // position to the other column proportionally (the two documents may have
    // different total heights, so we sync by fractional position).
    const leftScrollRef = React.useRef(null);
    const rightScrollRef = React.useRef(null);
    const syncingRef = React.useRef(false); // prevent scroll → sync → scroll loops

    React.useEffect(() => {
        if (!showRight) return;
        const leftEl = leftScrollRef.current;
        const rightEl = rightScrollRef.current;
        if (!leftEl || !rightEl) return;

        const syncScroll = (source, target) => () => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            const sourceMax = source.scrollHeight - source.clientHeight;
            const targetMax = target.scrollHeight - target.clientHeight;
            if (sourceMax > 0 && targetMax > 0) {
                target.scrollTop = (source.scrollTop / sourceMax) * targetMax;
            }
            syncingRef.current = false;
        };

        const onLeftScroll = syncScroll(leftEl, rightEl);
        const onRightScroll = syncScroll(rightEl, leftEl);
        leftEl.addEventListener('scroll', onLeftScroll, {passive: true});
        rightEl.addEventListener('scroll', onRightScroll, {passive: true});
        return () => {
            leftEl.removeEventListener('scroll', onLeftScroll);
            rightEl.removeEventListener('scroll', onRightScroll);
        };
    }, [showRight]);

    const handleDividerMouseDown = React.useCallback((e) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const onMouseMove = (moveEvent) => {
            if (!isDragging.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const rawPx = moveEvent.clientX - rect.left + containerRef.current.scrollLeft;
            const clampedPx = Math.max(MIN_LEFT_PX, Math.min(rect.width - MIN_RIGHT_PX, rawPx));
            setLeftPercent((clampedPx / rect.width) * 100);
        };

        const onMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }, []);

    if (!showRight) {
        return (
            <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                {left}
            </Box>
        );
    }

    return (
        <Stack
            ref={containerRef}
            direction="row"
            sx={{flex: 1, minHeight: 0, width: '100%', overflowX: 'auto', overflowY: 'hidden'}}
        >
            {/* Left — English */}
            <Box
                sx={{
                    flex: `0 0 ${leftPercent}%`,
                    minWidth: MIN_LEFT_PX,
                    minHeight: 0,
                    display: 'grid',
                    gridTemplateRows: 'auto minmax(0, 1fr)',
                    overflow: 'hidden',
                }}
            >
                <LanguageHeader label={leftLabel} color="primary"/>
                <Box ref={leftScrollRef} sx={{minHeight: 0, overflowY: 'auto'}}>{left}</Box>
            </Box>

            {/* Vertical divider */}
            <Box
                onMouseDown={handleDividerMouseDown}
                sx={(theme) => ({
                    flexShrink: 0,
                    width: 5,
                    cursor: 'col-resize',
                    bgcolor: theme.palette.divider,
                    position: 'relative',
                    zIndex: 10,
                    transition: 'background-color 0.15s',
                    '&:hover': {bgcolor: theme.palette.primary.main},
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 3,
                        height: 32,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.55)',
                        pointerEvents: 'none',
                    },
                })}
            />

            {/* Right — original language */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: MIN_RIGHT_PX,
                    minHeight: 0,
                    display: 'grid',
                    gridTemplateRows: 'auto minmax(0, 1fr)',
                    overflow: 'hidden',
                }}
            >
                <LanguageHeader label={rightLabel} color="default"/>
                <Box ref={rightScrollRef} sx={{minHeight: 0, overflowY: 'auto'}}>{right}</Box>
            </Box>
        </Stack>
    );
};

export default DualLanguagePanes;
